import React, {FocusEventHandler, useMemo} from 'react';
import {CustomFieldFormComponentProps} from 'module/CustomField/type/CFComponentType';
import {CustomFieldGroupedMembersComponent} from 'module/CustomField/component/CustomFieldGroupedMembersComponent'
import {getCFDefGrid} from 'module/CustomField/util/getCFDefGrid'
import {css} from '@emotion/react'
import {useTheme} from '@mui/material'
import {filterCFErrorsGlobal} from 'module/CustomField/util/filterCFErrors'
import {getPosFromCFMember} from 'module/CustomField/util/CustomField'

const cssGrid = css`
	display: grid;
	justify-content: start;
	align-items: end; // match input in favor of label
	width: 100%;
	gap: 2px
`

/**
 * wrap in a <form/> element to enable correct focussing
 */
export const CustomFieldFormComponent = React.memo<CustomFieldFormComponentProps>(({
	model, persistedModel, customFieldDefinitions, validationResult, actionPending, changePermitted, forceEnableText,
	setCFValue, addCFValue, deleteCFValue, moveCFValue, fetchCFAttribute, validateForm,
}) => {
	const theme = useTheme()
	let {groupedCFDefCells, maxColSpan} = useMemo(() =>
			getCFDefGrid((customFieldDefinitions || []).filter(cfDef => cfDef.visible)),
		[JSON.stringify(customFieldDefinitions)])
	if (!model) return null;
	//merge positions to pos attribute
	if (model.cf) model = {...model, cf: model.cf.map(m => ({...m, pos: getPosFromCFMember(m)}))}
	const onFocus: FocusEventHandler<HTMLElement> = (evt) => {
		if (evt.target.tagName === 'INPUT' || evt.target.tagName === 'TEXTAREA') {
			const target = evt.target as HTMLInputElement
			if (target.type !== 'checkbox') {
				evt.target.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'})
			}
		}
	}
	const cssGlobalError = css`
		background: ${theme.palette.error.main};
		color: ${theme.palette.error.contrastText};
		border-radius: ${theme.shape.borderRadius}px;
		padding: 4px 8px;
	`
	const globalValidationErrors = filterCFErrorsGlobal(validationResult, customFieldDefinitions)
	return <div className={'CustomFieldFormComponent'}>
		<div className={'CustomFieldFormGlobalErrors'}>
			{globalValidationErrors.map((err, i) => <div key={i} css={cssGlobalError}>{err.message}</div>)}
		</div>
		<div
			className={'CustomFieldFormGrid'}
			css={[
				cssGrid,
				css`grid-template-columns: repeat(${maxColSpan}, minmax(120px, 1fr));`,
			]}
			onFocus={onFocus}>
			{groupedCFDefCells.map(({cfDefCells, groupId, rowPos, colPos, rowSpan, colSpan}, i) => {
				return <div
					key={i}
					className={'CustomFieldFormMember'}
					css={css`
						grid-area: ${rowPos} / ${colPos} / span 1 / span ${colSpan};
					`}
				>
					<CustomFieldGroupedMembersComponent
						groupId={groupId}
						cfDefCells={cfDefCells}
						model={model}
						persistedModel={persistedModel}
						validationResult={validationResult}
						setCFValue={setCFValue}
						addCFValue={addCFValue}
						deleteCFValue={deleteCFValue}
						moveCFValue={moveCFValue}
						fetchCFAttribute={fetchCFAttribute}
						validateForm={validateForm}
						forceEnableText={forceEnableText}
						changePermitted={changePermitted}
						actionPending={actionPending}
						colSpan={colSpan}
						rowSpan={rowSpan}
					/>
				</div>
			})}
		</div>
	</div>
})